import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Typography, TextField, Button, Box, Grid, CircularProgress, Card, CardContent, Stack, InputAdornment, IconButton
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, ArrowDropDown as ArrowDropDownIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import movMonedaService, { MovMoneda } from '../../services/movMonedaService';
import TipoMonedaSearch from '../../components/TipoMonedaSearch';
import { TipoMoneda } from '../../services/tipoMonedaService';

const MovMonedaForm: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { codigoMoneda: codigoParam, fechaCotizacion: fechaParam } = useParams();
  const isEdit = !!(codigoParam && fechaParam);

  const [formData, setFormData] = useState<MovMoneda>({
    codigoMoneda: 0,
    fechaCotizacion: new Date().toISOString().split('T')[0],
    cotizacion: 0,
    nombreMoneda: null,
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [monedaSearchOpen, setMonedaSearchOpen] = useState(false);

  const loadMovMoneda = useCallback(async () => {
    if (!isEdit || !codigoParam || !fechaParam) return;
    try {
      setLoading(true);
      const mov = await movMonedaService.getMovMoneda(
        Number(codigoParam),
        decodeURIComponent(fechaParam)
      );
      setFormData(mov);
    } catch {
      enqueueSnackbar('Error al cargar la cotización de moneda', { variant: 'error' });
      navigate('/sistema/movmoneda');
    } finally {
      setLoading(false);
    }
  }, [isEdit, codigoParam, fechaParam, enqueueSnackbar, navigate]);

  useEffect(() => {
    if (isEdit) loadMovMoneda();
  }, [isEdit, loadMovMoneda]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'cotizacion') {
      setFormData((prev) => ({ ...prev, cotizacion: value === '' ? 0 : Number(value) }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleMonedaSelect = (moneda: TipoMoneda) => {
    const label = moneda.descripcion?.trim() || `Moneda ${moneda.codigoMoneda}`;
    setFormData((prev) => ({
      ...prev,
      codigoMoneda: moneda.codigoMoneda,
      nombreMoneda: label,
    }));
    setMonedaSearchOpen(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.codigoMoneda) {
      enqueueSnackbar('Debe seleccionar una moneda', { variant: 'warning' });
      return;
    }
    if (!formData.cotizacion || formData.cotizacion <= 0) {
      enqueueSnackbar('La cotización debe ser mayor a 0', { variant: 'warning' });
      return;
    }
    if (!formData.fechaCotizacion) {
      enqueueSnackbar('La fecha es requerida', { variant: 'warning' });
      return;
    }

    const hoy = new Date();
    hoy.setHours(23, 59, 59, 999);
    const fechaSeleccionada = new Date(`${formData.fechaCotizacion}T12:00:00`);
    if (fechaSeleccionada > hoy) {
      enqueueSnackbar('La fecha no puede ser futura', { variant: 'warning' });
      return;
    }

    try {
      setSaving(true);
      if (isEdit && codigoParam && fechaParam) {
        await movMonedaService.updateMovMoneda(
          Number(codigoParam),
          decodeURIComponent(fechaParam),
          formData
        );
      } else {
        await movMonedaService.createMovMoneda(formData);
      }
      enqueueSnackbar('Cotización guardada exitosamente', { variant: 'success' });
      navigate('/sistema/movmoneda');
    } catch (error: unknown) {
      console.error('Error al guardar cotización:', error);
      const err = error as { response?: { data?: string | { message?: string } } };
      const msg = typeof err?.response?.data === 'string'
        ? err.response.data
        : err?.response?.data?.message || 'Error al guardar la cotización';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const monedaLabel = formData.nombreMoneda
    ? `${formData.codigoMoneda} - ${formData.nombreMoneda}`
    : formData.codigoMoneda
      ? String(formData.codigoMoneda)
      : '';

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={1.5}
            sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}
          >
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/sistema/movmoneda')}
              sx={{ textTransform: 'none' }}
            >
              Cotizaciones de moneda
            </Button>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
              {isEdit ? 'Editar cotización de moneda' : 'Nueva cotización de moneda'}
            </Typography>
          </Stack>

          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Moneda"
                  value={monedaLabel}
                  fullWidth
                  required
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setMonedaSearchOpen(true)}
                          edge="end"
                          size="small"
                          title="Buscar moneda"
                          disabled={isEdit}
                        >
                          <ArrowDropDownIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  name="fechaCotizacion"
                  label="Fecha de cotización"
                  type="date"
                  value={formData.fechaCotizacion}
                  onChange={handleChange}
                  fullWidth
                  required
                  disabled={isEdit}
                  InputLabelProps={{ shrink: true }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  name="cotizacion"
                  label="Cotización"
                  type="number"
                  value={formData.cotizacion || ''}
                  onChange={handleChange}
                  fullWidth
                  required
                  inputProps={{ step: '0.0001', min: '0' }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
            </Grid>

            <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                {saving ? <CircularProgress size={24} /> : 'Guardar'}
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/sistema/movmoneda')}
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                Cancelar
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>

      <TipoMonedaSearch
        open={monedaSearchOpen}
        onClose={() => setMonedaSearchOpen(false)}
        onSelect={handleMonedaSelect}
      />
    </Box>
  );
};

export default MovMonedaForm;
